import { Component, effect, NgZone, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { AlertController, Platform } from '@ionic/angular';

import { DriverResponse } from './models/driver.model';
import { MenuTabs } from './models/menu.model';

import { SqliteStorageService } from './services/sqlite-storage/sqlite-storage.service';
import { CookieService } from "ngx-cookie-service";
import { CoreService } from './services/core/core.service';
import { LoaderService } from './services/loader/loader.service';
import { UserProvider } from './services/providers/user-provider.ts/user-provider';
import { AuthInterceptorHelper } from './services/auth-interceptor-helper/auth-interceptor-helper';
import { PushNotificationService } from './services/push-notification/push-notification.service';
import { NgcCookieConsentService } from "ngx-cookieconsent";
import { take } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { StatusBar, Style } from '@capacitor/status-bar';
import { DeclarationLifecycleService } from './services/declaration-lifecycle/declaration-lifecycle.service';
import { App } from '@capacitor/app';
import { isValidSsoExchangeToken } from './helpers/security/trusted-sso-redirect';
import { RouterOutletRefService } from './services/router-outlet-ref/router-outlet-ref.service';

const COOKIE_OPTION = { expires: 365, secure: true, sameSite: 'Strict' as const };

@Component({
  selector: 'app-root',
  templateUrl: 'app.component.html',
  styleUrls: ['app.component.scss'],
  standalone: false,
})
export class AppComponent implements OnInit {

  // Global authentication & platform flags
  isLoggedIn = false;
  isMobileApp = false;
  footerFlag = false;

  // Tracks the timestamp of the last back-press on an exit page (for double-tap-to-exit)
  private lastBackPressTime: number | null = null;
  private isExitAlertOpen = false;
  private readonly DASHBOARD_PAGE = '/dashboard';
  private readonly AUTO_EXIT_PAGES = ['/login', '/login-v2'];
  private readonly EXIT_INTERVAL_MS = 2000;
  // Minimum gap to distinguish an intentional double-tap from duplicate rapid firings
  private readonly DEBOUNCE_MS = 300;
  // Logged-in driver profile data
  driverData: DriverResponse | null = null;

  // Menu data used by desktop and mobile layouts
  menuItems: MenuTabs[] = [];
  staticMenuItems: MenuTabs[] = [];
  menuTabs: MenuTabs[] = [];
  isMenuDisabled = false;

  // Side menu position (desktop)
  menuDirection: 'start' | 'end' = 'start';

  // Cookies
  isCookieExist: boolean = false;
  cookieValue: string = '';

  constructor(
    private readonly sqliteStorageService: SqliteStorageService,
    readonly loaderService: LoaderService,
    private readonly router: Router,
    private readonly coreService: CoreService,
    private readonly authHelper: AuthInterceptorHelper,
    private readonly ccService: NgcCookieConsentService,
    private readonly translateService: TranslateService,
    private readonly platform: Platform,
    private readonly alertController: AlertController,
    private readonly routerOutletRef: RouterOutletRefService,
    private readonly userProvider: UserProvider,
    private readonly pushNotification: PushNotificationService,
    private readonly cookieService: CookieService,
    private readonly declarationLifecycleService: DeclarationLifecycleService,
    private readonly ngZone: NgZone
  ) {
    this.initializeUniversalLinks();
    this.initializePlatform();
    this.bindReactiveState();
  }

  ngOnInit() {
    this.handleAndroidStatusBar();
    if (navigator.onLine) {
      this.declarationLifecycleService.initializeDeclarationCheck();
    }
    this.ccService.initializing$.subscribe(
      (event: any) => {
        // the cookieconsent is initializing... Not yet safe to call methods like `NgcCookieConsentService.hasAnswered()`
        console.log(`initializing: ${JSON.stringify(event)}`);
      });
    this.checkForCookies();
  }

  async initializeUniversalLinks() {

    // App already running
    App.addListener('appUrlOpen', ({ url }) => {
      this.handleUniversalLink(url);
    });

    // App launched from closed state
    const launchUrl = await App.getLaunchUrl();

    if (launchUrl?.url) {
      this.handleUniversalLink(launchUrl.url);
    }
  }

  private handleUniversalLink(urlString: string) {

    const url = new URL(urlString);

    if (url.pathname === '/auth/callback/mobile') {

      const token = url.searchParams.get('token');

      // Validate token format against an allowlist before routing.
      // Prevents attacker-controlled deep-link tokens from reaching the exchange flow.
      if (!token || !isValidSsoExchangeToken(token)) {
        return;
      }

      this.ngZone.run(() => {

        this.router.navigate(
          ['/auth/callback/web'],
          {
            queryParams: {
              token
            }
          }
        );

      });
    }
  }

  async handleAndroidStatusBar() {
    if (this.coreService.isMobileApp && !this.coreService.isIos) {
      // Force dark icons (black text/icons) regardless of system dark mode
      await StatusBar.setStyle({ style: Style.Light });

      // Keep background white
      await StatusBar.setBackgroundColor({ color: '#FFFFFF' });

      // Prevent overlay so content starts below status bar
      await StatusBar.setOverlaysWebView({ overlay: true });
    }
  }


  // Initializes app-level platform flags
  private initializePlatform(): void {
    this.isMobileApp = this.coreService.isMobileApp;

    this.platform.ready().then(async () => {
      if (this.isMobileApp) {
        await this.pushNotification.requestPushPermission();
        this.pushNotification.initializeListeners();
        this.registerBackButtonHandler();
      }

      const currentUser = await this.sqliteStorageService.getItem('currentUser');
      const pathname = globalThis.location.pathname;

      if (
        pathname.includes('/web/not-my-vehicle/') ||
        pathname.includes('/not-my-vehicle-web')
      ) {
        const segments = pathname.split('/');
        const lastSegment = segments.at(- 1);
        this.footerFlag = true;
        this.router.navigate(['/web/not-my-vehicle', lastSegment]);
      } else if (currentUser) {
        this.isLoggedIn = true;
        this.router.navigateByUrl('/dashboard');
      }

    })
  }

  // Handles Android hardware back button and swipe-back on mobile.
  // Priority 200 ensures this handler fires before Ionic's overlay handlers (priority ~100).
  // For non-exit pages, processNextHandler() is called to delegate back to Ionic's own
  // IonRouterOutlet navigation handler, which correctly manages the outlet stack.
  private registerBackButtonHandler(): void {
    this.platform.backButton.subscribeWithPriority(200, async (processNextHandler) => {
      const currentUrl = this.router.url.split('?')[0];

      // Auto-exit pages (login / SSO) → immediate exit, no prompt
      const isAutoExitPage =
        this.AUTO_EXIT_PAGES.includes(currentUrl) ||
        currentUrl.startsWith('/LoginSSO/');

      if (isAutoExitPage) {
        App.exitApp();
        return;
      }

      // Dashboard → confirm alert with double-tap-to-exit
      if (currentUrl === this.DASHBOARD_PAGE) {
        const now = Date.now();
        const elapsed = this.lastBackPressTime !== null ? now - this.lastBackPressTime : null;

        // Ignore duplicate rapid firings from the same physical press (< 300 ms)
        if (elapsed !== null && elapsed < this.DEBOUNCE_MS) {
          return;
        }

        // Second intentional press within 2 s → dismiss any open alert and exit
        if (elapsed !== null && elapsed <= this.EXIT_INTERVAL_MS) {
          const openAlert = await this.alertController.getTop();
          if (openAlert) await openAlert.dismiss();
          App.exitApp();
          return;
        }

        // Alert already presenting → block duplicate
        if (this.isExitAlertOpen) {
          return;
        }

        // First press → record timestamp synchronously, then show alert
        this.lastBackPressTime = now;
        this.isExitAlertOpen = true;
        await this.showExitConfirmAlert();
        return;
      }

      // All other pages → navigate back via the IonRouterOutlet's own view stack.
      // pop() is the same method Ionic uses natively — it correctly handles
      // the outlet's internal stack regardless of browser history state.
      processNextHandler();
    });

  }

  private async showExitConfirmAlert(): Promise<void> {
    const alert = await this.alertController.create({
      message: this.translateService.instant("EXIT_MSG"),
      backdropDismiss: false,
      buttons: [
        {
          text: this.translateService.instant("FUEL_LABEL5"),
          role: 'cancel',
          cssClass: 'bg-light-cancel',
          handler: () => {
            this.lastBackPressTime = null;
          },
        },
        {
          text: this.translateService.instant("RD_MASTER_ES_LABEL13"),
          handler: () => {
            App.exitApp();
          },
        },
      ],
    });
    await alert.present();
    // Reset the flag once the alert is dismissed by any means
    // (Cancel button, OK button, or programmatic dismiss on double-tap)
    await alert.onDidDismiss();
    this.isExitAlertOpen = false;
  }

  checkForCookies() {
    const ccWindow = document.querySelector('.cc-window') as HTMLElement | null;
    if (!ccWindow) {
      console.error('Cookie window not found in DOM');
      return;
    }

    if (this.isMobileApp) {
      ccWindow.classList.add("ion-hide", 'd-none');
      return;
    }

    this.isCookieExist = this.cookieService.check('cc-ari');
    this.cookieValue = 'cc_Functional,cc_Statistics,cc_Marketing';

    if (this.isCookieExist) {
      // One-time migration: re-write the existing cookie with Secure + SameSite=Strict
      // attributes if the version flag cc-ari-m is absent. This covers users who accepted
      // consent before these attributes were added. Once cc-ari-m is written, this
      // block is skipped on all future visits so no unnecessary re-writes occur.
      if (!this.cookieService.check('cc-ari-m')) {
        const existingValue = this.cookieService.get('cc-ari');
        this.cookieService.set('cc-ari', existingValue, COOKIE_OPTION);
        this.cookieService.set('cc-ari-m', '2', COOKIE_OPTION);
      }
      this.hideCookieBanner(ccWindow);
    } else {
      this.showCookieBanner(ccWindow);
    }

    this.attachCookieEvents();
    this.updateCookieMsg();

  }

  updateCookieMsg(): void {
    const ccSettBtn = document.querySelector('.cc-show-settings') as HTMLElement | null;
    const ccAccBtn = document.querySelector('.cc-accept-all') as HTMLElement | null;
    const ccMsg = document.querySelector('#cmsg') as HTMLElement | null;
    const lblList = document.querySelectorAll('.cc_chkbx_label') as NodeListOf<HTMLElement>;

    this.translateService
      .get([
        'COOKIE_MSG',
        'FUNC_BTN',
        'STAT_BTN',
        'MAR_BTN',
        'ACCPT_BTN',
        'SETT_BTN',
        'SS_BTN',
      ])
      .pipe(take(1)) // prevents memory leaks
      .subscribe({
        next: (data: any) => {
          // Null-safe updates
          if (ccSettBtn) {
            ccSettBtn.textContent = data['SETT_BTN'];
          }

          if (ccAccBtn) {
            ccAccBtn.textContent = data['ACCPT_BTN'];
          }

          if (ccMsg) {
            ccMsg.textContent = data['COOKIE_MSG'];
          }

          // Safe label updates
          if (lblList?.length >= 3) {
            lblList[0].textContent = data['FUNC_BTN'];
            lblList[1].textContent = data['STAT_BTN'];
            lblList[2].textContent = data['MAR_BTN'];
          }
        },
        error: (err: any) => {
          console.error('Translation error in cookie message:', err);
        },
      });
  }

  private attachCookieEvents(): void {
    const ccSettBtn = document.querySelector('.cc-show-settings');
    const ccAccBtn = document.querySelector('.cc-accept-all');

    ccSettBtn?.addEventListener('click', (event) => this.customizeCookies(event));
    ccAccBtn?.addEventListener('click', () => this.saveCookies());
  }

  saveCookies() {
    this.ccService.close(false);
    this.cookieService.set('cc-ari', this.cookieValue, COOKIE_OPTION);
    // Write version flag so the migration check in checkForCookies() is skipped.
    this.cookieService.set('cc-ari-m', '2', COOKIE_OPTION);
  }

  customizeCookies(event: Event): void {
    const target = event.target as HTMLElement;

    if (!target) return;

    // Toggle button label (translation-safe)
    const isShowSettings = target.innerText.trim() === this.translateService.instant('SETT_BTN');
    const newLabel = isShowSettings
      ? this.translateService.instant('SS_BTN') // Save settings
      : this.translateService.instant('SETT_BTN'); // Show settings

    target.textContent = newLabel;

    // Toggle category container safely
    const btnContainer = document.querySelector('.cmplz-categories-wrap') as HTMLElement | null;
    btnContainer?.classList.toggle('hide-content');

    // Get checked checkbox values safely
    const checkedList = Array.from(
      document.querySelectorAll("input[type='checkbox']:checked")
    ) as HTMLInputElement[];

    this.cookieValue = checkedList.map(cb => cb.value).join(',');

    // (Optional) If you actually meant labels, not inputs:
    const labelList = document.querySelectorAll('.cc_chkbx_label') as NodeListOf<HTMLElement>;

    if (labelList.length >= 3) {
      labelList[0].textContent = this.translateService.instant('FUNC_BTN');
      labelList[1].textContent = this.translateService.instant('STAT_BTN');
      labelList[2].textContent = this.translateService.instant('MAR_BTN');
    }
  }

  private hideCookieBanner(ccWindow: HTMLElement): void {
    ccWindow.classList.add('ion-hide', 'd-none');

    this.ccService.close(false);
    this.ccService.fadeOut();
  }

  private showCookieBanner(ccWindow: HTMLElement): void {
    ccWindow.classList.remove('ion-hide', 'd-none');

    this.ccService.open();
    this.ccService.fadeIn();
  }

  // Subscribes to signal-based global state
  // Keeps app shell (menu, header, footer) in sync
  private bindReactiveState(): void {

    effect(() => {
      this.isLoggedIn = this.userProvider.readIsLoggedIn();
    });

    effect(() => {
      this.menuItems = this.userProvider.readMenuItems();
    });

    effect(() => {
      this.staticMenuItems = this.userProvider.readStaticMenuItems();
    });

    effect(() => {
      this.menuTabs = this.userProvider.readMenuTabs();
    });

    effect(() => {
      this.driverData = this.userProvider.readDriverData();
    });

    effect(() => {
      this.isMenuDisabled = this.userProvider.readMenuDisabled();
    });
  }

  // Centralized logout handler
  logout(): void {
    this.authHelper.logout();
  }

  // Utility helper used by templates
  parseNumber(value: string): number {
    return Number(value);
  }
}
